import { createApiRef } from '@backstage/core-plugin-api';
import { AxiosInstance, AxiosResponse } from 'axios';
import { AxiosInstanceProviderApi } from './axiosInstanceApi';

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }

  return btoa(binary);
};

const normalizeEncodedPayload = (payload: string): string => {
  let normalized = payload.trim().replace(/^['"]|['"]$/g, '');

  if (normalized.includes('%')) {
    try {
      normalized = decodeURIComponent(normalized);
    } catch {
      // Keep original payload when decode fails.
    }
  }

  return normalized.replace(/\s+/g, '');
};

export interface UserApi {
  getUserInfoByEmail: (email: string) => Promise<AxiosResponse>;
  refreshToken: () => Promise<AxiosResponse>;
  getImage: (email: string) => Promise<string | undefined>;
  getDetails: (email: string) => Promise<AxiosResponse>;
}

export const userApiRef = createApiRef<UserApi>({
  id: 'cbre.devx.api.user',
});

export class UserApiImpl implements UserApi {
  private readonly axiosInstancePromise: Promise<AxiosInstance>;

  constructor(axiosInstanceProviderApi: AxiosInstanceProviderApi) {
    this.axiosInstancePromise = axiosInstanceProviderApi.getInstance();
  }

  async getUserInfoByEmail(email: string): Promise<AxiosResponse> {
    const instance = await this.axiosInstancePromise;
    return instance.get(`service/discovery/userinfo`, {
      params: {
        user: email,
      },
    });
  }

  async refreshToken(): Promise<AxiosResponse> {
    const instance = await this.axiosInstancePromise;
    return instance
      .get('auth/microsoft/refresh', {
        params: {
          env: 'development',
          optional: true,
        },
        headers: {
          'x-requested-with': 'XMLHttpRequest',
        },
      })
      .then(async res => {
        this.setIdTokenCookie(res.data);
        return res;
      });
  }

  async getImage(email: string): Promise<string | undefined> {
    const instance = await this.axiosInstancePromise;

    return instance
      .get('ms-graph/images', {
        params: {
          email,
        },
        responseType: 'arraybuffer',
      })
      .then(res => {
        const contentType =
          res?.headers?.['content-type'] ||
          res?.headers?.['Content-Type'] ||
          'image/jpeg';

        if (`${contentType}`.includes('text/html')) {
          return undefined;
        }

        if (res?.data instanceof ArrayBuffer) {
          return `data:${String(contentType).split(';')[0]};base64,${arrayBufferToBase64(res.data)}`;
        }

        if (typeof res?.data === 'string') {
          const normalized = normalizeEncodedPayload(res.data);
          if (/^data:image\//i.test(normalized)) {
            return normalized;
          }
          return `data:${String(contentType).split(';')[0]};base64,${normalized}`;
        }

        return undefined;
      })
      .catch(() => undefined);
  }
  async getDetails(email: string): Promise<AxiosResponse> {
    const instance = await this.axiosInstancePromise;

    return instance
      .get('ms-graph/user-details', {
        params: {
          email,
        },
      })
      .then(res => res.data)
      .catch(err => {});
  }
  async setIdTokenCookie(data: any) {
    const instance = await this.axiosInstancePromise;

    return instance.get('cookie', {
      headers: {
        Authorization: `Bearer ${data?.backstageIdentity?.token}`,
      },
    });
  }
}
